/**
 * Shared utilities for parsing inline Ask references used in markdown:
 * - [hexVis:<cellId>]
 * - [hexProject:<projectId>]
 * - [hexCell:<cellId>] (legacy tag for cell references)
 * - C01, C10, etc. (bare cell references)
 *
 * Keep this in sync with the agent prompts.
 *
 * Adding a new reference tag type? Make sure to:
 * 1. Declare a constant here and extend the regex below.
 * 2. Teach the remark plugin in remarkHexInlineReferences.ts how to handle the tag.
 * 3. Allow the tag's attributes in MarkdownSanitizeSchema.ts.
 * 4. Render the tag inside InlineMarkdownRenderer.tsx.
 * 5. Update downstream renderers (sharedMessageFormatting.ts, agentSlackbotUtils.ts, MCP formatters, etc.) so secondary channels understand the new tag.
 * 6. Cover the parser + renderer behavior with tests since these paths are too easy to break.
 */

import { HexId } from "../idTypeBrands.js";

export const HEX_VIS_TAG = "hexVis";
export const HEX_PROJECT_TAG = "hexProject";
export const HEX_CELL_TAG = "hexCell";

export const getReferenceRegex = (): RegExp =>
  new RegExp(
    // Optional backtick before and after the bracketed reference (e.g., `[hexCell:C01]`)
    `\`?\\[(${HEX_VIS_TAG}|${HEX_PROJECT_TAG}|${HEX_CELL_TAG}):([a-zA-Z0-9_-]{1,64})(?::([^\\]]+))?\\]\`?`,
    "g",
  );

// Matches bare cell references like C01, C10, etc.
// Supports optional backtick wrapping (e.g., `C01`)
// Uses lookbehind/lookahead to ensure word boundaries or backticks
const bareCellReferenceRegex =
  /(?<=^|[^a-zA-Z0-9])`?C\d{1,3}`?(?=$|[^a-zA-Z0-9])/g;

export type InlineReferenceTextChunk = {
  type: "MARKDOWN";
  content: string;
};

export type InlineReferenceCellChunk = {
  type: "CELL_REFERENCE";
  /** This may be a staticCellId or a cellId (pre-January 2026) */
  cellId: string;
  /**
   * Optional, decoded label segment from `[hexCell:<id>:<encodedLabel>]`.
   *
   * Note: This is only available for tagged references, not for bare references like `C01`.
   * Consumers should treat it as a best-effort display hint (e.g. for missing cells).
   */
  label?: string;
  referenceTag: typeof HEX_VIS_TAG | typeof HEX_CELL_TAG;
};

export type InlineReferenceProjectChunk = {
  type: "PROJECT_REFERENCE";
  hexId: HexId;
};

export type InlineReferenceChunk =
  | InlineReferenceTextChunk
  | InlineReferenceCellChunk
  | InlineReferenceProjectChunk;

/**
 * Parses a markdown string and returns a sequence of chunks separating inline
 * Ask references from plain text. The parser preserves the original order and
 * content of non-reference text.
 */
export function parseInlineReferences(
  markdown: string,
): InlineReferenceChunk[] {
  const chunks: InlineReferenceChunk[] = [];

  const referenceRegex = getReferenceRegex();
  referenceRegex.lastIndex = 0;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Add text before the match, if any
  while ((match = referenceRegex.exec(markdown)) != null) {
    if (match.index > lastIndex) {
      const textContent = markdown.slice(lastIndex, match.index);
      chunks.push({ type: "MARKDOWN", content: textContent });
    }

    const patternType = match[1];
    const id = match[2];
    const rawLabel = match[3];
    if (!patternType || !id) {
      continue;
    }

    if (patternType === HEX_VIS_TAG || patternType === HEX_CELL_TAG) {
      const referenceTag =
        patternType === HEX_VIS_TAG ? HEX_VIS_TAG : HEX_CELL_TAG;
      const label = decodeInlineReferenceLabel(rawLabel);
      chunks.push({
        type: "CELL_REFERENCE",
        cellId: id,
        ...(label != null ? { label } : {}),
        referenceTag,
      });
    } else if (patternType === HEX_PROJECT_TAG) {
      chunks.push({ type: "PROJECT_REFERENCE", hexId: id as HexId });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < markdown.length) {
    chunks.push({ type: "MARKDOWN", content: markdown.slice(lastIndex) });
  }

  // If nothing matched, return the full markdown as a single text chunk
  if (chunks.length === 0) {
    chunks.push({ type: "MARKDOWN", content: markdown });
  }

  return parseBareCellReferences(chunks);
}

const parseBareCellReferences = (
  chunks: InlineReferenceChunk[],
): InlineReferenceChunk[] => {
  const result: InlineReferenceChunk[] = [];

  for (const chunk of chunks) {
    if (chunk.type !== "MARKDOWN") {
      result.push(chunk);
      continue;
    }

    const text = chunk.content;
    bareCellReferenceRegex.lastIndex = 0;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = bareCellReferenceRegex.exec(text)) != null) {
      if (match.index > lastIndex) {
        result.push({
          type: "MARKDOWN",
          content: text.slice(lastIndex, match.index),
        });
      }

      // Strip backticks if present (e.g., `C01` -> C01)
      const cellId = match[0].replace(/`/g, "");

      result.push({
        type: "CELL_REFERENCE",
        cellId,
        referenceTag: HEX_CELL_TAG,
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex === 0) {
      result.push(chunk);
    } else if (lastIndex < text.length) {
      result.push({ type: "MARKDOWN", content: text.slice(lastIndex) });
    }
  }

  return result;
};

export function encodeInlineReferenceLabel(label: string): string {
  return encodeURIComponent(label);
}

export function decodeInlineReferenceLabel(
  encodedLabel: string | undefined,
): string | undefined {
  if (!encodedLabel) {
    return undefined;
  }
  try {
    return decodeURIComponent(encodedLabel);
  } catch {
    return encodedLabel;
  }
}
